import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  ChevronDown, Link2, Copy, Check, Search, X, ZoomIn,
  Cpu, Car, Home, Bed, Box, ClipboardCheck, Book, Wifi, Server, Wrench, Map,
  Fuel, Key, Users, Radio, Download, Terminal, Settings, Zap, Globe,
  AlertTriangle, Info, Menu, Skull, Dog,
} from "lucide-react";
import { api } from "@/lib/api";

// -------- shared icon map (also imported by the admin editor) --------
// `Zombie` is a Guide-only alias for lucide's Skull — closest thematic match
// while keeping the icon library minimal.
export const GUIDE_ICON_MAP = {
  Cpu, Car, Home, Bed, Box, ClipboardCheck, Book, Wifi, Server, Wrench, Map,
  Fuel, Key, Users, Radio, Download, Terminal, Settings, Zap, Globe,
  AlertTriangle, Info, Zombie: Skull, Dog,
};

const DEFAULT_ACCENT = "#C0A875";
const HEADER_OFFSET = 80;

// -------- deep-link copy button (same pattern as Rules) --------
function copyDeepLink(anchor) {
  const url = `${window.location.origin}/guide#${anchor}`;
  const done = () => toast.success("Link copied — paste it in Discord.");
  const fail = () => toast.error("Couldn't copy — long-press the URL bar instead.");
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(done, fail);
    return;
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    done();
  } catch {
    fail();
  }
}

function CopyLinkButton({ anchor, accent = "#8D9A87", label }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        copyDeepLink(anchor);
      }}
      title={label || "Copy deep-link to this section"}
      aria-label={label || `Copy link to ${anchor}`}
      data-testid={`guide-copy-link-${anchor}`}
      className="shrink-0 w-8 h-8 border border-[#283524] hover:border-[#C0A875] flex items-center justify-center transition-colors"
      style={{ color: accent }}
    >
      <Link2 size={13} />
    </button>
  );
}

// -------- copy IP / copy port pill --------
function CopyValue({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const doCopy = () => {
    const done = () => {
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done, () => toast.error("Copy failed"));
      return;
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={testid}
      className="group text-left bg-[#0D110C] border border-[#283524] hover:border-[#C0A875] transition-colors p-4 flex items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="label-tag text-[#5C6B51]">{label}</div>
        <div className="mt-1 font-mono-ui text-[#E2E4DF] text-base sm:text-lg truncate">{value}</div>
      </div>
      <div className="w-10 h-10 border border-[#283524] group-hover:border-[#C0A875] flex items-center justify-center text-[#8D9A87] group-hover:text-[#C0A875] transition-colors">
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </div>
    </button>
  );
}

// -------- individual content block renderers --------
import Markdown from "./Markdown";
import SubsectionDivider from "./SubsectionDivider";

function Paragraph({ block }) {
  return (
    <div className="text-[#A8B5A0] text-sm sm:text-base leading-relaxed max-w-3xl">
      <Markdown>{block.text}</Markdown>
    </div>
  );
}

function Steps({ block, accent }) {
  return (
    <ol className="space-y-2 max-w-3xl">
      {(block.items || []).map((it, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="shrink-0 w-6 h-6 border flex items-center justify-center font-mono-ui text-[10px]"
            style={{ borderColor: accent, color: accent }}
          >
            {i + 1}
          </span>
          <span className="text-[#A8B5A0] text-sm leading-relaxed pt-0.5">
            <Markdown inline>{it}</Markdown>
          </span>
        </li>
      ))}
    </ol>
  );
}

function CodeBlock({ block }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(block.code || "").then(() => {
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="relative bg-[#0D110C] border border-[#283524] max-w-3xl group">
      {block.lang && (
        <div className="px-3 py-1.5 border-b border-[#283524] font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
          {block.lang}
        </div>
      )}
      <pre className="p-4 font-mono-ui text-xs text-[#E2E4DF] overflow-x-auto whitespace-pre">
{block.code}
      </pre>
      <button
        type="button"
        onClick={doCopy}
        className="absolute top-2 right-2 w-8 h-8 border border-[#283524] bg-[#161D14] text-[#8D9A87] hover:text-[#C0A875] hover:border-[#C0A875] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
        title="Copy code"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function ImageBlock({ block }) {
  const [zoomed, setZoomed] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoomed]);

  return (
    <>
      <figure className="max-w-3xl">
        <div className="relative group border border-[#283524] bg-[#0D110C] inline-block">
          <img
            src={block.src}
            alt={block.alt || ""}
            loading="lazy"
            onClick={() => setZoomed(true)}
            className="block max-h-[320px] w-auto cursor-zoom-in"
          />
          <button
            type="button"
            onClick={() => setZoomed(true)}
            title="Zoom in"
            aria-label="Zoom image"
            data-testid="guide-image-zoom"
            className="absolute top-2 right-2 w-9 h-9 border border-[#283524] bg-[#0D110C]/85 backdrop-blur-sm text-[#C0A875] hover:bg-[#C0A875]/20 hover:border-[#C0A875] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <ZoomIn size={14} />
          </button>
        </div>
        {block.caption && (
          <figcaption className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
            {block.caption}
          </figcaption>
        )}
      </figure>

      {zoomed && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image"
          onClick={() => setZoomed(false)}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
          data-testid="guide-image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            title="Close (Esc)"
            aria-label="Close zoomed image"
            className="absolute top-4 right-4 w-10 h-10 border border-[#283524] bg-[#0D110C]/85 text-[#E2E4DF] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center"
          >
            <X size={16} />
          </button>
          <img
            src={block.src}
            alt={block.alt || ""}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "95vw", height: "90vh", objectFit: "contain" }}
            className="border border-[#283524] shadow-2xl cursor-default bg-[#0D110C]"
          />
          {block.caption && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87] bg-[#0D110C]/85 border border-[#283524] px-3 py-1.5 max-w-[90vw] truncate">
              {block.caption}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

const CALLOUT_STYLES = {
  info:   { border: "border-[#7EC4CF]/50", bg: "bg-[#7EC4CF]/5",  text: "text-[#7EC4CF]", Icon: Info,           label: "INFO" },
  tip:    { border: "border-[#4ADE80]/50", bg: "bg-[#4ADE80]/5",  text: "text-[#4ADE80]", Icon: Info,           label: "TIP"  },
  warn:   { border: "border-yellow-700/50", bg: "bg-yellow-900/10", text: "text-yellow-400", Icon: AlertTriangle, label: "WARN" },
  danger: { border: "border-[#C0392B]/60", bg: "bg-[#C0392B]/10", text: "text-[#E27B7B]", Icon: AlertTriangle,  label: "DANGER" },
};

function Callout({ block }) {
  const s = CALLOUT_STYLES[block.kind] || CALLOUT_STYLES.info;
  const { Icon } = s;
  return (
    <div className={`border ${s.border} ${s.bg} p-4 flex gap-3 max-w-3xl`}>
      <div className={`${s.text} shrink-0 pt-0.5`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-mono-ui text-[10px] uppercase tracking-[0.25em] ${s.text}`}>{s.label}</div>
        <div className="mt-1 text-[#E2E4DF] text-sm leading-relaxed">
          <Markdown>{block.text}</Markdown>
        </div>
      </div>
    </div>
  );
}

function Table({ block, accent }) {
  const headers = block.headers || [];
  const rows = block.rows || [];
  if (!headers.length && !rows.length) return null;
  return (
    <div className="max-w-3xl overflow-x-auto border border-[#283524]">
      <table className="w-full text-left font-mono-ui text-xs">
        {headers.length > 0 && (
          <thead>
            <tr className="bg-[#0D110C] border-b border-[#283524]">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2 uppercase tracking-[0.15em]" style={{ color: accent }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={`${ri % 2 === 0 ? "bg-[#161D14]" : "bg-[#0F140E]"} border-b border-[#283524] last:border-b-0`}>
              {row.map((c, ci) => (
                <td key={ci} className="px-3 py-2 text-[#A8B5A0]">
                  <Markdown inline>{c}</Markdown>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- dispatch --------
export function BlockRenderer({ block, accent }) {
  switch (block.type) {
    case "paragraph": return <Paragraph block={block} />;
    case "steps":     return <Steps block={block} accent={accent} />;
    case "code":      return <CodeBlock block={block} />;
    case "image":     return <ImageBlock block={block} />;
    case "callout":   return <Callout block={block} />;
    case "table":     return <Table block={block} accent={accent} />;
    default:          return null;
  }
}

// -------- sidebar nav --------
function NavList({ sections, activeId, onClick, className = "" }) {
  const slug = (s) =>
    String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const subId = (sec, sub, si) => `${sec.id}--${slug(sub.title) || `sub-${si + 1}`}`;

  return (
    <ul className={className} data-testid="guide-nav-list">
      {sections.map((s) => {
        const activeSection = activeId === s.id;
        // The nav treats the section as "current" when either the section
        // heading itself is active OR any of its subsections is active — so
        // the child list stays visible while the user scrolls through it.
        const isCurrent =
          activeSection ||
          (s.subsections || []).some((sub, i) => sub.title && subId(s, sub, i) === activeId);
        const Icon = GUIDE_ICON_MAP[s.icon] || Book;
        const accent = s.color || DEFAULT_ACCENT;
        const titledSubs = (s.subsections || [])
          .map((sub, i) => ({ sub, i }))
          .filter(({ sub }) => sub.title);
        return (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onClick(s.id)}
              data-testid={`guide-nav-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-l-2 -ml-px lg:-ml-[2px] ${
                activeSection
                  ? ""
                  : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#5C6B51]"
              }`}
              style={activeSection ? {
                borderColor: accent,
                color: accent,
                backgroundColor: `${accent}12`,
              } : undefined}
            >
              <Icon size={14} className="shrink-0" style={{ color: activeSection ? accent : undefined }} />
              <div className="min-w-0 flex-1">
                <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] opacity-70">
                  {s.number}
                </div>
                <div className="font-mono-ui text-[11px] uppercase tracking-[0.15em] truncate">
                  {s.title}
                </div>
              </div>
            </button>

            {/* Nested subsection links — only shown when this section is
                the current one, and only when it has 2+ titled subsections
                (a single subsection would just duplicate the section link). */}
            {isCurrent && titledSubs.length >= 2 && (
              <ul
                className="ml-[38px] mt-0.5 mb-2 py-1 space-y-[1px]"
                data-testid={`guide-nav-subs-${s.id}`}
              >
                {titledSubs.map(({ sub, i }) => {
                  const sid = subId(s, sub, i);
                  const activeSub = activeId === sid;
                  const subAccent = sub.title_color || accent;
                  return (
                    <li key={sid}>
                      <button
                        type="button"
                        onClick={() => onClick(sid)}
                        data-testid={`guide-nav-sub-${sid}`}
                        className={`w-full flex items-center gap-2.5 text-left px-2 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.18em] truncate rounded-sm transition-colors ${
                          activeSub
                            ? "font-semibold"
                            : "text-[#5C6B51] hover:text-[#E2E4DF] hover:bg-[#161D14]"
                        }`}
                        style={activeSub ? { color: subAccent, background: `${subAccent}10` } : undefined}
                      >
                        {/* Bullet — filled + colored when active, hollow slate when idle */}
                        <span
                          aria-hidden="true"
                          className="shrink-0 rounded-full border transition-all"
                          style={{
                            width: activeSub ? 8 : 6,
                            height: activeSub ? 8 : 6,
                            background: activeSub ? subAccent : "transparent",
                            borderColor: activeSub ? subAccent : "#5C6B51",
                            borderWidth: activeSub ? 0 : 1.5,
                          }}
                        />
                        <span className="truncate">{sub.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// -------- main component --------
export default function Guide() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState(() => new Set());
  const [activeId, setActiveId] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [query, setQuery] = useState("");
  const sectionRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    api.get("/content/guide")
      .then((res) => { if (!cancelled) setContent(res.data?.content || null); })
      .catch(() => { /* keep null → will show empty state */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sections = content?.sections || [];
  const serverInfo = content?.server_info || {};
  const meta = content?.meta || {};

  // Set to a timestamp when the user click-jumps; scroll-spy ignores itself
  // for a brief window afterwards so the smooth-scroll can't drift the
  // highlight past the intended section (mirrors Rules.jsx).
  const jumpLockUntil = useRef(0);

  // Text-search filter. We walk every block's plain text into one blob per
  // section, then keep sections whose title, subsection title, or content
  // includes the query. When a section survives, we also drop any
  // subsection whose blob doesn't match — so a long guide collapses to
  // exactly what the searcher cares about.
  const q = query.trim().toLowerCase();
  const blockText = (b) => {
    if (!b) return "";
    if (b.type === "paragraph" || b.type === "callout") return b.text || "";
    if (b.type === "steps") return (b.items || []).join(" ");
    if (b.type === "code") return `${b.lang || ""} ${b.code || ""}`;
    if (b.type === "image") return `${b.alt || ""} ${b.caption || ""}`;
    if (b.type === "table") {
      return [...(b.headers || []), ...((b.rows || []).flat())].join(" ");
    }
    return "";
  };
  const subText = (sub) =>
    `${sub.title || ""} ${(sub.blocks || []).map(blockText).join(" ")}`.toLowerCase();
  const filteredSections = useMemo(() => {
    if (!q) return sections;
    return sections
      .map((s) => {
        const secBlob = `${s.title || ""} ${s.number || ""}`.toLowerCase();
        const matchingSubs = (s.subsections || []).filter((sub) => subText(sub).includes(q));
        // If any sub matches, keep the section with only those subs.
        // If the section title itself matches (but no sub does), keep the
        // section intact so the searcher sees full context.
        if (matchingSubs.length > 0) return { ...s, subsections: matchingSubs };
        if (secBlob.includes(q)) return s;
        return null;
      })
      .filter(Boolean);
  }, [sections, q]);

  const displaySections = q ? filteredSections : sections;

  // Build a stable subsection id from `${section.id}--${slugified-title}`.
  // Only titled subsections get an id/nav entry — untitled ones would be
  // impossible to label in the sidebar.
  const slug = (s) =>
    String(s || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40);
  const subsectionId = (sec, sub, si) => `${sec.id}--${slug(sub.title) || `sub-${si + 1}`}`;

  // Look up a section by any id (section id OR one of its subsection ids).
  // Used when jumpTo receives a subsection id and needs to auto-open its
  // parent section.
  const parentSectionFor = (id) => {
    for (const s of displaySections) {
      if (s.id === id) return s;
      const subs = s.subsections || [];
      for (let i = 0; i < subs.length; i++) {
        if (subs[i].title && subsectionId(s, subs[i], i) === id) return s;
      }
    }
    return null;
  };

  // Flat list of every scroll-spy target, interleaved section → its subsections.
  const allIds = useMemo(() => {
    const ids = [];
    for (const s of displaySections) {
      ids.push(s.id);
      (s.subsections || []).forEach((sub, i) => {
        if (sub.title) ids.push(subsectionId(s, sub, i));
      });
    }
    return ids;
  }, [displaySections]);

  // When there's an active search, open every surviving section so the
  // matches are immediately visible.
  useEffect(() => {
    if (!q) return;
    setOpenSections(new Set(displaySections.map((s) => s.id)));
  }, [q, displaySections]);

  // Auto-open first section + honour URL hash on load
  useEffect(() => {
    if (!sections.length) return;
    const hash = window.location.hash.replace("#", "");
    const initial = hash && allIds.includes(hash) ? hash : sections[0].id;
    setOpenSections(new Set([initial]));
    setActiveId(initial);
    if (hash) {
      // small delay for layout
      setTimeout(() => {
        const el = document.getElementById(initial);
        if (el) window.scrollTo({ top: el.offsetTop - HEADER_OFFSET, behavior: "instant" });
      }, 100);
    }
  }, [content]);

  // Scroll-spy — mirrors Rules.jsx. Uses getBoundingClientRect so activation
  // stays reliable even when Lenis smooth-scroll is in flight, and honours
  // jumpLockUntil so a click can't be immediately overridden.
  useEffect(() => {
    if (!sections.length) return;
    const onScroll = () => {
      if (Date.now() < jumpLockUntil.current) return;
      let current = allIds[0];
      const threshold = HEADER_OFFSET + 24;
      for (const id of allIds) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - threshold <= 0) current = id;
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [allIds, sections.length]);

  const toggle = (id) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const jumpTo = (id) => {
    // If target is a subsection, we still need to open its PARENT section
    // so the anchor lands on visible content. If target is a section, this
    // just opens that section.
    const parent = parentSectionFor(id) || { id };
    setOpenSections((prev) => (prev.has(parent.id) ? prev : new Set(prev).add(parent.id)));
    // Eagerly highlight the target — scroll-spy alone won't fire if the
    // section is already near the header (mirrors Rules.jsx behavior).
    setActiveId(id);
    // Suppress scroll-spy for ~1s while Lenis smoothly scrolls — otherwise
    // for short sections momentum can drift into the next and steal focus.
    jumpLockUntil.current = Date.now() + 1000;
    setMobileNavOpen(false);
    // Wait one frame so the section actually has height when we scroll.
    requestAnimationFrame(() => {
      const target = sectionRefs.current[id];
      if (!target) return;
      if (window.__lenis) {
        window.__lenis.scrollTo(target, { offset: -HEADER_OFFSET, duration: 0.8 });
      } else {
        const y = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#8D9A87] font-mono-ui text-sm">
        // Loading survivor's guide…
      </div>
    );
  }

  return (
    <section className="bg-[#0D110C] py-16 sm:py-24" data-testid="guide-root">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="block label-tag text-[#C0A875]">// SURVIVOR'S FIELD GUIDE</span>
          <h2 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            The Guide
          </h2>
          <p className="mt-4 font-mono-ui text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8D9A87]">
            {meta.last_updated && <>Last Updated: <span className="text-[#C0A875]">{meta.last_updated}</span></>}
          </p>
        </div>

        {/* Connection block — just IP + port */}
        <div className="mt-8 bg-[#161D14] border border-[#283524] p-5 sm:p-6" data-testid="guide-connect-block">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 border border-[#C0A875] text-[#C0A875] flex items-center justify-center">
              <Server size={16} />
            </div>
            <div>
              <div className="label-tag text-[#5C6B51]">// CONNECTION</div>
              <h3 className="font-display uppercase text-[#E2E4DF] text-lg sm:text-xl tracking-tight">
                Join in Zomboid
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CopyValue label="Server IP" value={serverInfo.ip} testid="guide-copy-ip" />
            <CopyValue label="Port" value={serverInfo.port} testid="guide-copy-port" />
          </div>
          {(!serverInfo.ip && !serverInfo.port) && (
            <p className="mt-3 font-mono-ui text-xs text-[#5C6B51]">
              // No connection info set — an admin can add it from the Guide editor.
            </p>
          )}
        </div>

        {/* Body: sidebar + accordion sections */}
        <div className="mt-8 mb-6">
          <div className="relative max-w-xl">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B51]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide…"
              data-testid="guide-search-input"
              className="w-full bg-[#161D14] border border-[#283524] focus:border-[#C0A875] text-[#E2E4DF] pl-9 pr-9 py-3 font-mono-ui text-sm focus:outline-none placeholder:text-[#5C6B51]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="guide-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-[#8D9A87] hover:text-[#C0A875]"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {query && (
            <p className="mt-2 label-tag text-[#5C6B51]">
              // {displaySections.length} section{displaySections.length === 1 ? "" : "s"} matched
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-10">
          {/* Mobile nav toggle */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setMobileNavOpen((v) => !v)}
              className="inline-flex items-center gap-2 border border-[#283524] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]"
              data-testid="guide-mobile-nav-toggle"
            >
              {mobileNavOpen ? <X size={12} /> : <Menu size={12} />}
              {mobileNavOpen ? "Close nav" : "Section nav"}
            </button>
            {mobileNavOpen && (
              <NavList
                sections={displaySections}
                activeId={activeId}
                onClick={jumpTo}
                className="mt-2 bg-[#161D14] border border-[#283524] divide-y divide-[#283524]"
              />
            )}
          </div>

          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="label-tag text-[#5C6B51] mb-3 px-4">// SECTIONS</div>
              <NavList
                sections={displaySections}
                activeId={activeId}
                onClick={jumpTo}
                className="border-l border-[#283524]"
              />
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-4 min-w-0">
            {displaySections.map((sec) => {
              const isOpen = openSections.has(sec.id);
              const Icon = GUIDE_ICON_MAP[sec.icon] || Book;
              const accent = sec.color || DEFAULT_ACCENT;
              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  ref={(el) => (sectionRefs.current[sec.id] = el)}
                  data-testid={`guide-section-${sec.id}`}
                  className="bg-[#161D14] border scroll-mt-24 transition-colors"
                  style={{ borderColor: isOpen ? accent : "#283524" }}
                >
                  <div className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 sm:py-5 hover:bg-[#1B221A] transition-colors">
                    <button
                      type="button"
                      onClick={() => toggle(sec.id)}
                      aria-expanded={isOpen}
                      className="flex items-center gap-4 text-left flex-1 min-w-0"
                      data-testid={`guide-toggle-${sec.id}`}
                    >
                      <div
                        className="w-10 h-10 border flex items-center justify-center shrink-0 transition-colors"
                        style={{
                          borderColor: isOpen ? accent : "#283524",
                          color: isOpen ? accent : "#8D9A87",
                        }}
                      >
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block label-tag text-[#5C6B51]">SECTION {sec.number}</span>
                        <h3 className="mt-1 font-display uppercase text-[#E2E4DF] text-lg sm:text-xl tracking-tight">
                          {sec.title}
                        </h3>
                      </div>
                      <ChevronDown
                        size={20}
                        className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        style={{ color: isOpen ? accent : "#8D9A87" }}
                      />
                    </button>
                    <CopyLinkButton anchor={sec.id} accent={isOpen ? accent : "#8D9A87"} label={`Copy link to ${sec.title}`} />
                  </div>

                  {isOpen && (
                    <div className="border-t p-5 sm:p-6 space-y-6" style={{ borderColor: `${accent}55` }}>
                      {(sec.subsections || []).map((sub, si) => {
                        const subColor = sub.title_color || accent;
                        const sizeClass = {
                          compact: "text-base sm:text-lg",
                          huge: "text-3xl sm:text-4xl",
                        }[sub.title_size] || "text-2xl sm:text-3xl"; // normal default
                        // Divider color: explicit override wins; otherwise
                        // accent/glyph use the subsection color and other
                        // styles fall back to the section frame color.
                        const dividerStyle = sub.divider || "line";
                        const dividerColor =
                          sub.divider_color ||
                          (dividerStyle === "accent" || dividerStyle === "glyph"
                            ? subColor
                            : "#283524");
                        const subId = sub.title ? subsectionId(sec, sub, si) : null;
                        return (
                          <div key={si} className="space-y-4">
                            {si > 0 && (
                              <SubsectionDivider style={dividerStyle} color={dividerColor} />
                            )}
                            {sub.title && (
                              <h4
                                id={subId}
                                ref={(el) => { if (subId) sectionRefs.current[subId] = el; }}
                                className={`font-display uppercase font-bold tracking-tight pb-1 scroll-mt-24 ${sizeClass}`}
                                style={{
                                  color: subColor,
                                  textDecoration: sub.title_underline ? "underline" : undefined,
                                  textDecorationColor: sub.title_underline ? `${subColor}66` : undefined,
                                  textUnderlineOffset: sub.title_underline ? "6px" : undefined,
                                  textDecorationThickness: sub.title_underline ? "2px" : undefined,
                                }}
                              >
                                {sub.title}
                              </h4>
                            )}
                            {(sub.blocks || []).map((b, bi) => (
                              <BlockRenderer key={bi} block={b} accent={subColor} />
                            ))}
                          </div>
                        );
                      })}
                      {(sec.subsections || []).length === 0 && (
                        <p className="font-mono-ui text-xs text-[#5C6B51]">
                          // This section is empty — an admin can fill it in from the Guide editor.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {displaySections.length === 0 && q && (
              <div className="bg-[#161D14] border border-[#283524] p-8 text-center">
                <div className="label-tag text-[#5C6B51]">// NO MATCHES</div>
                <p className="mt-2 text-[#8D9A87] text-sm font-mono-ui">
                  Nothing in the guide matches <span className="text-[#C0A875]">"{query}"</span>.
                </p>
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="mt-3 inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em]"
                >
                  Clear search
                </button>
              </div>
            )}
            {sections.length === 0 && !q && (
              <p className="text-[#8D9A87] text-sm font-mono-ui">// No guide sections yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
